import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import { BASE_URL } from "../../config";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faSave,
  faSpinner,
  faSyncAlt,
  faPlus,
  faTrash,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("authentication/token") ||
    localStorage.getItem("auth_token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const data = error?.response?.data;

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  if (data?.message && typeof data.message === "object") {
    const firstField = Object.values(data.message)[0];
    if (Array.isArray(firstField) && firstField[0]) {
      return String(firstField[0]);
    }
    if (typeof firstField === "string" && firstField.trim()) {
      return firstField;
    }
  }

  return fallbackMessage;
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));

const normalizeAmount = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasAmountDifference = (a, b) =>
  Math.abs(normalizeAmount(a) - normalizeAmount(b)) > 0.009;

const roomTypes = [
  "Ward",
  "Semi-Private",
  "Private",
  "Regular Private",
  "Large Private",
  "Suite",
  "ICU",
];

const miscParticulars = [
  "Laboratory",
  "Medicines",
  "Supplies",
  "Uncovered Items",
];

const physicianRoles = [
  "Attending Physician",
  "Surgeon",
  "Assistant Surgeon",
  "Reader",
  "Specialist",
  "Anesthesiologist",
];

const getBenefitEditorType = (benefit) => {
  const desc = String(benefit?.benefit_desc || "").toLowerCase();

  if (desc.includes("anesthesiologist fee")) return "PHYSICIAN_SIMPLE";
  if (desc.includes("basic consultation")) return "PHYSICIAN_SIMPLE";
  if (desc.includes("pre & post natal")) return "PHYSICIAN_SIMPLE";
  if (desc.includes("clinic setting")) return "PHYSICIAN_AND_CPT";
  if (desc.includes("emergency")) return "PHYSICIAN_AND_CPT";
  if (desc.includes("laboratory")) return "PHYSICIAN_AND_CPT";
  if (desc.includes("misc. hospital services")) return "MISC";
  if (desc.includes("room and board")) return "RNB";
  if (desc.includes("physician fee")) return "PHYSICIAN_FEE";
  if (desc.includes("surgical fee")) return "SURGICAL";
  if (desc.includes("opd-or")) return "OPD_OR";

  if (
    desc.includes("coinsure") ||
    desc.includes("deductible") ||
    desc.includes("expense type") ||
    desc.includes("philhealth") ||
    desc.includes("pmi")
  ) {
    return "PLACEHOLDER";
  }

  return "PLACEHOLDER";
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getPhysicianTotals = (rows) => {
  const totalAmount = rows.reduce((sum, row) => sum + toNumber(row.amount), 0);
  const totalPhilhealth = rows.reduce(
    (sum, row) => sum + toNumber(row.philhealth),
    0
  );
  return {
    totalAmount,
    totalPhilhealth,
    net: totalAmount - totalPhilhealth,
  };
};

const getCptTotal = (rows) => rows.reduce((sum, row) => sum + toNumber(row.cpt_rate), 0);

const getRnbCoveredTotal = (rows) =>
  rows.reduce((sum, row) => sum + toNumber(row.covered), 0);

const buildCustomPhysicianOption = (name) => {
  const trimmedName = String(name || "").trim();
  if (!trimmedName) return null;

  return {
    value: `custom:${trimmedName}`,
    label: trimmedName,
    physician_id: null,
    specialization: "",
    isCustom: true,
  };
};

const CPT_REQUIRED_PHYSICIAN_AND_CPT_BENEFITS = new Set([
  "EGQIHVQB58", // Laboratory
  "CFDEASKQ19", // Emergency
  "HYTJ8K1JTI", // Clinic Setting
]);

const OPD_OR_BENEFIT_CODE = "KEKMDSWADP";

const modalOverlayClass =
  "fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6";
const modalCardClass =
  "max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-white shadow-2xl";

const amountInputClass =
  "w-full rounded-lg border border-gray-200 px-2 py-1 text-right text-sm focus:border-orange-500 focus:outline-none";

const SmallAmountInput = ({ value, onChange, placeholder = "0.00" }) => (
  <input
    type="number"
    step="0.01"
    min="0"
    className={amountInputClass}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
  />
);

const iconButtonClass =
  "inline-flex items-center justify-center rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50";

const BenefitEditorModal = ({
  open,
  benefit,
  editorType,
  state,
  loading,
  saving,
  onClose,
  onSave,
  onStateChange,
  loadPhysicianOptions,
  loadCptOptions,
  loadRvuOptions,
}) => {
  if (!open || !benefit) return null;

  const physicianRows = state?.physicianRows || [];
  const cptRows = state?.cptRows || [];
  const miscRows = state?.miscRows || [];
  const rnbRows = state?.rnbRows || [];
  const ruvRows = state?.ruvRows || [];
  const uncovered = state?.uncovered || { amount: "", remarks: "" };

  const setRows = (key, rows) => onStateChange({ ...state, [key]: rows });

  const physicianTotals = getPhysicianTotals(physicianRows);
  const cptTotal = getCptTotal(cptRows);
  const miscTotal = miscRows.reduce((sum, row) => sum + toNumber(row.amount), 0);
  const rnbCovered = getRnbCoveredTotal(rnbRows);
  const ruvTotal = ruvRows.reduce((sum, row) => sum + toNumber(row.amount), 0);
  const ruvPhilhealth = ruvRows.reduce((sum, row) => sum + toNumber(row.philhealth), 0);

  const changeRowValue = (rows, index, field, value) => {
    const next = [...rows];
    next[index] = { ...next[index], [field]: value };
    return next;
  };

  const renderPhysicianRows = ({ withRole = false }) => (
    <div className="space-y-2">
      <div
        className={`grid gap-2 rounded-lg bg-gray-50 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 ${
          withRole ? "grid-cols-12" : "grid-cols-11"
        }`}
      >
        <div className={withRole ? "col-span-5" : "col-span-6"}>Physician</div>
        {withRole && <div className="col-span-2">Role</div>}
        <div className="col-span-2">Amount (PHP)</div>
        <div className="col-span-2">PhilHealth (PHP)</div>
        <div className="col-span-1 text-center">Action</div>
      </div>
      {physicianRows.map((row, index) => (
        <div
          key={`physician-row-${index}`}
          className={`grid gap-2 ${withRole ? "grid-cols-12" : "grid-cols-11"}`}
        >
          <div className={withRole ? "col-span-5" : "col-span-6"}>
            {(() => {
              const isManualPhysician = Boolean(
                row?.physicianOption?.isCustom ||
                (!row?.physician_id && String(row?.physician_name || "").trim())
              );

              return (
                <>
            <AsyncCreatableSelect
              cacheOptions
              defaultOptions
              value={row.physicianOption || null}
              loadOptions={loadPhysicianOptions}
              formatCreateLabel={(inputValue) =>
                `Use \"${String(inputValue || "").trim()}\" as custom physician`
              }
              isValidNewOption={(inputValue, _value, options) => {
                const keyword = String(inputValue || "").trim().toLowerCase();
                if (!keyword) return false;
                return !options.some(
                  (option) => String(option?.label || "").trim().toLowerCase() === keyword
                );
              }}
              onCreateOption={(inputValue) => {
                const customOption = buildCustomPhysicianOption(inputValue);
                if (!customOption) return;

                const next = changeRowValue(
                  physicianRows,
                  index,
                  "physicianOption",
                  customOption
                );

                next[index].physician_id = null;
                next[index].physician_name = customOption.label;
                next[index].specialization = "";

                setRows("physicianRows", next);
              }}
              onChange={(selected) => {
                const next = changeRowValue(
                  physicianRows,
                  index,
                  "physicianOption",
                  selected || null
                );
                next[index].physician_id = selected?.physician_id || null;
                next[index].physician_name = selected?.label || "";
                next[index].specialization = selected?.specialization || "";
                setRows("physicianRows", next);
              }}
              placeholder="Search or type physician"
              noOptionsMessage={({ inputValue }) =>
                String(inputValue || "").trim()
                  ? "No physician found. Press Enter to use typed name."
                  : "No physicians found"
              }
            />
                  {isManualPhysician && (
                    <div className="mt-1 inline-flex items-center rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                      Manual Physician
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {withRole && (
            <div className="col-span-2">
              <select
                className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                value={row.role || ""}
                onChange={(e) =>
                  setRows(
                    "physicianRows",
                    changeRowValue(physicianRows, index, "role", e.target.value)
                  )
                }
              >
                <option value="">Select role</option>
                {physicianRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col-span-2">
            <SmallAmountInput
              value={row.amount || ""}
              onChange={(e) =>
                setRows(
                  "physicianRows",
                  changeRowValue(physicianRows, index, "amount", e.target.value)
                )
              }
            />
          </div>
          <div className="col-span-2">
            <SmallAmountInput
              value={row.philhealth || ""}
              onChange={(e) =>
                setRows(
                  "physicianRows",
                  changeRowValue(physicianRows, index, "philhealth", e.target.value)
                )
              }
            />
          </div>
          <div className="col-span-1">
            <button
              type="button"
              className={iconButtonClass}
              onClick={() => {
                const next = physicianRows.filter((_, i) => i !== index);
                setRows("physicianRows", next);
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-50"
        onClick={() =>
          setRows("physicianRows", [
            ...physicianRows,
            {
              physician_id: "",
              physician_name: "",
              specialization: "",
              role: "",
              amount: "",
              philhealth: "",
              physicianOption: null,
            },
          ])
        }
      >
        <FontAwesomeIcon icon={faPlus} />
        Add Physician
      </button>
    </div>
  );

  const renderCptRows = () => (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2 rounded-lg bg-gray-50 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        <div className="col-span-8">Procedure (CPT)</div>
        <div className="col-span-3">Rate (PHP)</div>
        <div className="col-span-1 text-center">Action</div>
      </div>
      {cptRows.map((row, index) => (
        <div key={`cpt-row-${index}`} className="grid grid-cols-12 gap-2">
          <div className="col-span-8">
            <AsyncSelect
              cacheOptions
              defaultOptions
              value={row.cptOption || null}
              loadOptions={loadCptOptions}
              onChange={(selected) => {
                const next = changeRowValue(cptRows, index, "cptOption", selected || null);
                next[index].cpt_code = selected?.cpt_code || "";
                next[index].provider_cpt_code = selected?.provider_cpt_code || "";
                if (selected?.default_rate != null) {
                  next[index].cpt_rate = String(selected.default_rate);
                }
                setRows("cptRows", next);
              }}
              placeholder="Search CPT procedure"
            />
          </div>
          <div className="col-span-3">
            <SmallAmountInput
              value={row.cpt_rate || ""}
              onChange={(e) =>
                setRows("cptRows", changeRowValue(cptRows, index, "cpt_rate", e.target.value))
              }
            />
          </div>
          <div className="col-span-1">
            <button
              type="button"
              className={iconButtonClass}
              onClick={() => setRows("cptRows", cptRows.filter((_, i) => i !== index))}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
        onClick={() =>
          setRows("cptRows", [
            ...cptRows,
            { cpt_code: "", provider_cpt_code: "", cpt_rate: "", cptOption: null },
          ])
        }
      >
        <FontAwesomeIcon icon={faPlus} />
        Add CPT
      </button>
    </div>
  );

  const renderRnbRows = () => (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2 rounded-lg bg-gray-50 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        <div className="col-span-3">Room Type</div>
        <div className="col-span-2">Room #</div>
        <div className="col-span-2">Room Rate (PHP)</div>
        <div className="col-span-2">Days Confined</div>
        <div className="col-span-2">Covered Amount</div>
        <div className="col-span-1 text-center">Action</div>
      </div>
      {rnbRows.map((row, index) => {
        const rate = toNumber(row.room_rate);
        const days = toNumber(row.no_of_days);
        const covered = rate * days;

        return (
          <div key={`rnb-row-${index}`} className="grid grid-cols-12 gap-2">
            <div className="col-span-3">
              <select
                className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                value={row.rnb_desc || ""}
                onChange={(e) => {
                  const next = changeRowValue(rnbRows, index, "rnb_desc", e.target.value);
                  setRows("rnbRows", next);
                }}
              >
                <option value="">Room type</option>
                {roomTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <input
                type="text"
                className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                placeholder="Room #"
                value={row.room_no || ""}
                onChange={(e) =>
                  setRows("rnbRows", changeRowValue(rnbRows, index, "room_no", e.target.value))
                }
              />
            </div>
            <div className="col-span-2">
              <SmallAmountInput
                value={row.room_rate || ""}
                onChange={(e) =>
                  setRows("rnbRows", changeRowValue(rnbRows, index, "room_rate", e.target.value))
                }
              />
            </div>
            <div className="col-span-2">
              <input
                type="number"
                min="1"
                className="w-full rounded-lg border border-gray-200 px-2 py-2 text-right text-sm"
                value={row.no_of_days || ""}
                onChange={(e) =>
                  setRows("rnbRows", changeRowValue(rnbRows, index, "no_of_days", e.target.value))
                }
              />
            </div>
            <div className="col-span-2">
              <input
                type="text"
                readOnly
                className="w-full rounded-lg border border-green-200 bg-green-50 px-2 py-2 text-right text-sm font-semibold text-green-800"
                value={covered.toFixed(2)}
              />
            </div>
            <div className="col-span-1">
              <button
                type="button"
                className={iconButtonClass}
                onClick={() => setRows("rnbRows", rnbRows.filter((_, i) => i !== index))}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        );
      })}
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-lg border border-green-200 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-50"
        onClick={() =>
          setRows("rnbRows", [
            ...rnbRows,
            { rnb_desc: "", room_no: "", room_rate: "", no_of_days: "", covered: "" },
          ])
        }
      >
        <FontAwesomeIcon icon={faPlus} />
        Add R&B
      </button>
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">
          <FontAwesomeIcon icon={faSpinner} spin />
          Loading benefit details...
        </div>
      );
    }

    if (editorType === "PLACEHOLDER") {
      return (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          No editable fields for this benefit in phase 1. This modal is prepared as a placeholder for future enhancement.
        </div>
      );
    }

    if (editorType === "PHYSICIAN_SIMPLE") {
      return (
        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Physicians Details</h3>
            {renderPhysicianRows({ withRole: false })}
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            Total Amount: <span className="font-semibold">{formatCurrency(physicianTotals.totalAmount)}</span>
          </div>
        </div>
      );
    }

    if (editorType === "PHYSICIAN_FEE") {
      return (
        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Physician Fee Benefit</h3>
            {renderPhysicianRows({ withRole: false })}
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left">Physician ID</th>
                  <th className="px-2 py-2 text-left">Physician Name</th>
                  <th className="px-2 py-2 text-left">Specialization</th>
                  <th className="px-2 py-2 text-right">Professional Fee</th>
                </tr>
              </thead>
              <tbody>
                {physicianRows.map((row, idx) => (
                  <tr key={`physician-grid-${idx}`} className="border-t border-gray-100">
                    <td className="px-2 py-2">{row.physician_id || "-"}</td>
                    <td className="px-2 py-2">{row.physician_name || "-"}</td>
                    <td className="px-2 py-2">{row.specialization || "-"}</td>
                    <td className="px-2 py-2 text-right">{formatCurrency(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            Total Professional Fee: <span className="font-semibold">{formatCurrency(physicianTotals.totalAmount)}</span>
          </div>
        </div>
      );
    }

    if (editorType === "PHYSICIAN_AND_CPT") {
      const total = physicianTotals.net + cptTotal;
      return (
        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Physicians Details</h3>
            {renderPhysicianRows({ withRole: false })}
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">CPT Details</h3>
            {renderCptRows()}
          </div>
          <div className="grid grid-cols-1 gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 md:grid-cols-3">
            <div>Total Physician Fee: <span className="font-semibold">{formatCurrency(physicianTotals.net)}</span></div>
            <div>Total CPT Amount: <span className="font-semibold">{formatCurrency(cptTotal)}</span></div>
            <div>Total Amount: <span className="font-semibold text-green-700">{formatCurrency(total)}</span></div>
          </div>
        </div>
      );
    }

    if (editorType === "MISC") {
      return (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-800">Misc. Hospital Services</h3>
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 rounded-lg bg-gray-50 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              <div className="col-span-8">Particulars</div>
              <div className="col-span-3">Amount (PHP)</div>
              <div className="col-span-1 text-center">Action</div>
            </div>
            {miscRows.map((row, index) => (
              <div key={`misc-row-${index}`} className="grid grid-cols-12 gap-2">
                <div className="col-span-8">
                  <select
                    className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                    value={row.particulars || ""}
                    onChange={(e) =>
                      setRows(
                        "miscRows",
                        changeRowValue(miscRows, index, "particulars", e.target.value)
                      )
                    }
                  >
                    <option value="">Select particulars</option>
                    {miscParticulars.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-3">
                  <SmallAmountInput
                    value={row.amount || ""}
                    onChange={(e) =>
                      setRows(
                        "miscRows",
                        changeRowValue(miscRows, index, "amount", e.target.value)
                      )
                    }
                  />
                </div>
                <div className="col-span-1">
                  <button
                    type="button"
                    className={iconButtonClass}
                    onClick={() => setRows("miscRows", miscRows.filter((_, i) => i !== index))}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-50"
            onClick={() =>
              setRows("miscRows", [...miscRows, { particulars: "", amount: "" }])
            }
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Particular
          </button>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            Total Miscellaneous: <span className="font-semibold">{formatCurrency(miscTotal)}</span>
          </div>
        </div>
      );
    }

    if (editorType === "RNB") {
      return (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-800">Room &amp; Board Benefit</h3>
          {renderRnbRows()}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            Total Covered Amount: <span className="font-semibold">{formatCurrency(rnbCovered)}</span>
          </div>
        </div>
      );
    }

    if (editorType === "SURGICAL") {
      const physicianTotal = physicianTotals.net;
      const totalBenefit = physicianTotal + ruvTotal;

      return (
        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Surgical Fee Details (RUV)</h3>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 rounded-lg bg-gray-50 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <div className="col-span-4">RUV Description</div>
                <div className="col-span-2">R&amp;B</div>
                <div className="col-span-1">RUV Unit</div>
                <div className="col-span-2">Amount (PHP)</div>
                <div className="col-span-2">PhilHealth (PHP)</div>
                <div className="col-span-1 text-center">Action</div>
              </div>
              {ruvRows.map((row, index) => (
                <div key={`ruv-row-${index}`} className="grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      value={row.ruvOption || null}
                      loadOptions={loadRvuOptions}
                      onChange={(selected) => {
                        const next = changeRowValue(ruvRows, index, "ruvOption", selected || null);
                        next[index].ruv_code = selected?.rvu_code || "";
                        if (selected?.ruv_unit != null) {
                          next[index].ruv_unit = String(selected.ruv_unit);
                        }
                        setRows("ruvRows", next);
                      }}
                      placeholder="Search RUV"
                    />
                  </div>
                  <div className="col-span-2">
                    <select
                      className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                      value={row.rnb || ""}
                      onChange={(e) =>
                        setRows("ruvRows", changeRowValue(ruvRows, index, "rnb", e.target.value))
                      }
                    >
                      <option value="">R&B</option>
                      {roomTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-1">
                    <SmallAmountInput
                      value={row.ruv_unit || ""}
                      onChange={(e) =>
                        setRows("ruvRows", changeRowValue(ruvRows, index, "ruv_unit", e.target.value))
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <SmallAmountInput
                      value={row.amount || ""}
                      onChange={(e) =>
                        setRows("ruvRows", changeRowValue(ruvRows, index, "amount", e.target.value))
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <SmallAmountInput
                      value={row.philhealth || ""}
                      onChange={(e) =>
                        setRows(
                          "ruvRows",
                          changeRowValue(ruvRows, index, "philhealth", e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <button
                      type="button"
                      className={iconButtonClass}
                      onClick={() => setRows("ruvRows", ruvRows.filter((_, i) => i !== index))}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-2 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
              onClick={() =>
                setRows("ruvRows", [
                  ...ruvRows,
                  { ruv_code: "", remarks: "", rnb: "", ruv_unit: "", amount: "", philhealth: "", ruvOption: null },
                ])
              }
            >
              <FontAwesomeIcon icon={faPlus} />
              Add RUV
            </button>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Physicians Details</h3>
            {renderPhysicianRows({ withRole: true })}
          </div>

          <div className="grid grid-cols-1 gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 md:grid-cols-3">
            <div>Total RUV Amount: <span className="font-semibold">{formatCurrency(ruvTotal)}</span></div>
            <div>Total Physician: <span className="font-semibold">{formatCurrency(physicianTotal)}</span></div>
            <div>Total Benefit Amount: <span className="font-semibold text-green-700">{formatCurrency(totalBenefit)}</span></div>
          </div>
          <div className="text-xs text-gray-500">Total RUV PhilHealth: {formatCurrency(ruvPhilhealth)}</div>
        </div>
      );
    }

    if (editorType === "OPD_OR") {
      const totalIncurred = rnbCovered + cptTotal + physicianTotals.totalAmount;
      const totalPhilhealth = physicianTotals.totalPhilhealth + ruvPhilhealth;
      const excess = Math.max(0, totalIncurred - totalPhilhealth);
      const totalCovered = totalIncurred - totalPhilhealth;

      return (
        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Room &amp; Board Benefit</h3>
            {renderRnbRows()}
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Surgical Benefit</h3>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 rounded-lg bg-gray-50 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <div className="col-span-5">RUV Description</div>
                <div className="col-span-2">R&amp;B</div>
                <div className="col-span-1">RUV Unit</div>
                <div className="col-span-2">Amount (PHP)</div>
                <div className="col-span-1">PhilHealth</div>
                <div className="col-span-1 text-center">Action</div>
              </div>
              {ruvRows.map((row, index) => (
                <div key={`opd-ruv-row-${index}`} className="grid grid-cols-12 gap-2">
                  <div className="col-span-5">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      value={row.ruvOption || null}
                      loadOptions={loadRvuOptions}
                      onChange={(selected) => {
                        const next = changeRowValue(ruvRows, index, "ruvOption", selected || null);
                        next[index].ruv_code = selected?.rvu_code || "";
                        if (selected?.ruv_unit != null) {
                          next[index].ruv_unit = String(selected.ruv_unit);
                        }
                        setRows("ruvRows", next);
                      }}
                      placeholder="Search RUV"
                    />
                  </div>
                  <div className="col-span-2">
                    <select
                      className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                      value={row.rnb || ""}
                      onChange={(e) =>
                        setRows("ruvRows", changeRowValue(ruvRows, index, "rnb", e.target.value))
                      }
                    >
                      <option value="">R&B</option>
                      {roomTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-1">
                    <SmallAmountInput
                      value={row.ruv_unit || ""}
                      onChange={(e) =>
                        setRows("ruvRows", changeRowValue(ruvRows, index, "ruv_unit", e.target.value))
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <SmallAmountInput
                      value={row.amount || ""}
                      onChange={(e) =>
                        setRows("ruvRows", changeRowValue(ruvRows, index, "amount", e.target.value))
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <SmallAmountInput
                      value={row.philhealth || ""}
                      onChange={(e) =>
                        setRows(
                          "ruvRows",
                          changeRowValue(ruvRows, index, "philhealth", e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <button
                      type="button"
                      className={iconButtonClass}
                      onClick={() => setRows("ruvRows", ruvRows.filter((_, i) => i !== index))}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-2 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
              onClick={() =>
                setRows("ruvRows", [
                  ...ruvRows,
                  { ruv_code: "", remarks: "", rnb: "", ruv_unit: "", amount: "", philhealth: "", ruvOption: null },
                ])
              }
            >
              <FontAwesomeIcon icon={faPlus} />
              Add RUV
            </button>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Hospital Services / Lab Procedures</h3>
            {renderCptRows()}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Physician Fee Benefit</h3>
            {renderPhysicianRows({ withRole: false })}
          </div>

          <div className="rounded-xl border border-gray-200 p-3">
            <h3 className="mb-2 text-sm font-semibold text-gray-800">Uncovered Item</h3>
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-4">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Uncovered Amount (PHP)
                </label>
                <SmallAmountInput
                  value={uncovered.amount || ""}
                  onChange={(e) =>
                    onStateChange({
                      ...state,
                      uncovered: { ...uncovered, amount: e.target.value },
                    })
                  }
                />
              </div>
              <div className="col-span-8">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Remarks
                </label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  placeholder="Remarks"
                  value={uncovered.remarks || ""}
                  onChange={(e) =>
                    onStateChange({
                      ...state,
                      uncovered: { ...uncovered, remarks: e.target.value },
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 md:grid-cols-4">
            <div>Total Incurred: <span className="font-semibold">{formatCurrency(totalIncurred)}</span></div>
            <div>Total PhilHealth: <span className="font-semibold">{formatCurrency(totalPhilhealth)}</span></div>
            <div>Excess: <span className="font-semibold">{formatCurrency(excess)}</span></div>
            <div>Total Covered: <span className="font-semibold text-green-700">{formatCurrency(totalCovered)}</span></div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={modalOverlayClass}>
      <div className={modalCardClass}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit Benefit Details</h2>
            <p className="text-sm text-gray-500">
              {benefit.benefit_code} - {benefit.benefit_desc}
            </p>
          </div>
          <button type="button" className={iconButtonClass} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">{renderBody()}</div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-gray-200 bg-white px-5 py-4">
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
            onClick={onSave}
          >
            {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
            Save Details
          </button>
        </div>
      </div>
    </div>
  );
};

const RiBenefitsTab = ({
  transNo,
  memberId,
  initialIcdCode,
  riFormTotalAmount,
  onSaved,
  onDirtyChange,
}) => {
  const [allBenefits, setAllBenefits] = useState([]);
  const [benefitLimits, setBenefitLimits] = useState({});
  const [selectedBenefits, setSelectedBenefits] = useState({});
  const [icdCode, setIcdCode] = useState(initialIcdCode || "");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [activeBenefit, setActiveBenefit] = useState(null);
  const [editorState, setEditorState] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  const activeEditorType = useMemo(
    () => getBenefitEditorType(activeBenefit),
    [activeBenefit]
  );

  useEffect(() => {
    loadData();
  }, [transNo, memberId]);

  // Keep icd in sync when parent reloads transaction data
  useEffect(() => {
    if (initialIcdCode) setIcdCode(initialIcdCode);
  }, [initialIcdCode]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [benefitsRes, savedRes] = await Promise.all([
        axios.get(`${BASE_URL}/reimbursement/reference/benefits`, {
          headers: getAuthHeaders(),
        }),
        axios.get(
          `${BASE_URL}/reimbursement/transactions/${transNo}/benefits/saved`,
          { headers: getAuthHeaders() }
        ),
      ]);

      const available = benefitsRes?.data?.data?.benefits || [];
      setAllBenefits(available);

      const saved = savedRes?.data?.data?.benefits || [];

      // Pre-populate selected state from saved benefits
      const selectedMap = {};
      for (const b of saved) {
        selectedMap[b.benefit_code] = {
          checked: true,
          amount: String(b.amount || ""),
        };
      }
      setSelectedBenefits(selectedMap);

      // Load member benefit limits (non-critical)
      if (memberId) {
        axios
          .get(
            `${BASE_URL}/reimbursement/member/${memberId}/benefit-limits`,
            { headers: getAuthHeaders() }
          )
          .then((res) => {
            const limits = res?.data?.data?.benefit_limits || [];
            const limitsMap = {};
            for (const l of limits) {
              limitsMap[l.benefit_code] = l.total_benefit_limit;
            }
            setBenefitLimits(limitsMap);
          })
          .catch(() => {});
      }
    } catch (error) {
      if (error?.response?.status === 401) window.location.href = "/login";
      toast.error(getApiErrorMessage(error, "Failed to load benefits data."));
    } finally {
      setLoading(false);
    }
  };

  const toggleBenefit = (benefitCode) => {
    setIsDirty(true);
    setSelectedBenefits((prev) => {
      if (prev[benefitCode]?.checked) {
        const next = { ...prev };
        delete next[benefitCode];
        return next;
      }
      return { ...prev, [benefitCode]: { checked: true, amount: "" } };
    });
  };

  const setAmount = (benefitCode, amount) => {
    setIsDirty(true);
    setSelectedBenefits((prev) => ({
      ...prev,
      [benefitCode]: { ...(prev[benefitCode] || {}), checked: true, amount },
    }));
  };

  const selectedCount = Object.keys(selectedBenefits).filter(
    (k) => selectedBenefits[k]?.checked
  ).length;

  const totalAmount = Object.entries(selectedBenefits)
    .filter(([, v]) => v?.checked)
    .reduce((sum, [, v]) => sum + Number(v.amount || 0), 0);

  const handleSave = async () => {
    const benefitCodes = Object.entries(selectedBenefits)
      .filter(([, v]) => v?.checked)
      .map(([k]) => k);

    if (!benefitCodes.length) {
      toast.error("Select at least one benefit before saving.");
      return;
    }

    const amounts = benefitCodes.map((code) =>
      Number(selectedBenefits[code]?.amount || 0)
    );

    const benefitsTotal = amounts.reduce((sum, amount) => sum + amount, 0);
    const currentRiTotal = normalizeAmount(riFormTotalAmount);
    let updatedRiTotal = null;

    if (hasAmountDifference(currentRiTotal, benefitsTotal)) {
      const compareResult = await Swal.fire({
        icon: "question",
        title: "Total Claim Mismatch Detected",
        html: `
          <div style="text-align:left; font-size:13px; color:#1f2937;">
            <p style="margin-bottom:8px;">Magkaiba ang current values ng RI Form at Benefits computations.</p>
            <div style="border:1px solid #e5e7eb; border-radius:10px; padding:10px; background:#f8fafc; margin-bottom:8px;">
              <div><strong>Current RI Form - Total Amount of Claim:</strong> ${formatCurrency(currentRiTotal)}</div>
              <div><strong>Benefits & ICD - Total Claim:</strong> ${formatCurrency(benefitsTotal)}</div>
            </div>
            <p style="margin:0;">Gusto mo bang i-update ang <strong>Total Amount of Claim</strong> sa RI Form gamit ang bagong computed total from Benefits?</p>
          </div>
        `,
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Yes, Update RI Form Total",
        denyButtonText: "No, Keep Current RI Form Total",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#ea580c",
      });

      if (
        compareResult.isDismissed &&
        !compareResult.isDenied &&
        !compareResult.isConfirmed
      ) {
        return;
      }

      if (compareResult.isConfirmed) {
        updatedRiTotal = Number(benefitsTotal.toFixed(2));
      }
    }

    setSaving(true);
    try {
      const payload = {
        benefit_codes: benefitCodes,
        amounts,
        status: "saved",
        icd_code: icdCode,
      };

      if (updatedRiTotal != null) {
        payload.total_amt_of_claim = updatedRiTotal;
      }

      await axios.post(
        `${BASE_URL}/reimbursement/transactions/${transNo}/benefits/save`,
        payload,
        { headers: getAuthHeaders() }
      );
      toast.success("Benefits and ICD code saved.");
      setIsDirty(false);
      onSaved?.({
        updatedRiFormTotal: updatedRiTotal,
        benefitsTotal: Number(benefitsTotal.toFixed(2)),
      });
    } catch (error) {
      if (error?.response?.status === 401) window.location.href = "/login";
      toast.error(getApiErrorMessage(error, "Failed to save benefits."));
    } finally {
      setSaving(false);
    }
  };

  const loadPhysicianOptions = async (inputValue) => {
    const res = await axios.get(`${BASE_URL}/reimbursement/reference/physicians`, {
      headers: getAuthHeaders(),
      params: { q: inputValue || "", limit: 20 },
    });

    const rows = res?.data?.data?.physicians || [];
    return rows.map((row) => {
      const first = row.first_name || "";
      const middle = row.middle_name || "";
      const last = row.last_name || "";
      const fallbackName = [first, middle, last].filter(Boolean).join(" ").trim();

      return {
        value: row.physician_id,
        label: row.full_name || fallbackName || row.physician_name || "Unknown Physician",
        physician_id: row.physician_id,
        specialization: row.specialization || "",
      };
    });
  };

  const loadCptOptions = async (inputValue) => {
    const res = await axios.get(`${BASE_URL}/reimbursement/reference/cpt`, {
      headers: getAuthHeaders(),
      params: { q: inputValue || "", limit: 20 },
    });

    const rows = res?.data?.data?.cpt_codes || [];
    return rows.map((row) => ({
      value: row.cpt_code,
      label: `[${row.cpt_code}] ${row.cpt_desc || ""}`,
      cpt_code: row.cpt_code,
      provider_cpt_code: row.provider_cpt_code || row.cpt_code,
      default_rate: row.default_price || row.cpt_rate_major || 0,
    }));
  };

  const loadRvuOptions = async (inputValue) => {
    const res = await axios.get(`${BASE_URL}/reimbursement/reference/rvu`, {
      headers: getAuthHeaders(),
      params: { q: inputValue || "", limit: 20 },
    });

    const rows = res?.data?.data?.rvu || [];
    return rows.map((row) => ({
      value: row.rvu_code,
      label: `[${row.rvu_code}] ${row.rvu_description || ""}`,
      rvu_code: row.rvu_code,
      ruv_unit: row.ruv_unit || row.rvu_unit || 0,
      amount: row.amount || 0,
    }));
  };

  const mapPhysiciansForUi = (rows = []) =>
    rows.map((row) => ({
      physician_id: row.physician_id || "",
      physician_name: row.physician_name || "",
      specialization: row.specialization || "",
      role: row.role || "",
      amount: String(row.amount ?? ""),
      philhealth: String(row.philhealth ?? ""),
      physicianOption: row.physician_id
        ? {
            value: row.physician_id,
            label: row.physician_name || row.physician_id,
            physician_id: row.physician_id,
            specialization: row.specialization || "",
          }
        : buildCustomPhysicianOption(row.physician_name),
    }));

  const mapCptForUi = (rows = []) =>
    rows.map((row) => ({
      cpt_code: row.cpt_code || "",
      provider_cpt_code: row.provider_cpt_code || "",
      cpt_rate: String(row.cpt_rate ?? row.amount ?? ""),
      cptOption: row.cpt_code
        ? {
            value: row.cpt_code,
            label: `[${row.cpt_code}] ${row.cpt_desc || ""}`,
            cpt_code: row.cpt_code,
            provider_cpt_code: row.provider_cpt_code || row.cpt_code,
            default_rate: row.cpt_rate ?? row.amount ?? 0,
          }
        : null,
    }));

  const mapRuvForUi = (rows = []) =>
    rows.map((row) => ({
      ruv_code: row.ruv_code || row.rvu_code || "",
      remarks: row.remarks || "",
      rnb: row.rnb || "",
      ruv_unit: String(row.ruv_unit ?? row.rvu_unit ?? ""),
      amount: String(row.amount ?? ""),
      philhealth: String(row.philhealth ?? ""),
      ruvOption:
        row.rvu_code || row.ruv_code
          ? {
              value: row.ruv_code || row.rvu_code,
              label: `[${row.ruv_code || row.rvu_code}] ${row.rvu_description || ""}`,
              rvu_code: row.ruv_code || row.rvu_code,
              ruv_unit: row.ruv_unit || row.rvu_unit || 0,
            }
          : null,
    }));

  const openEditor = async (benefit) => {
    setActiveBenefit(benefit);
    setEditorOpen(true);
    setEditorLoading(true);
    setEditorState({
      physicianRows: [],
      cptRows: [],
      miscRows: [],
      rnbRows: [],
      ruvRows: [],
      uncovered: { amount: "", remarks: "" },
    });

    try {
      const type = getBenefitEditorType(benefit);
      const benefitCode = benefit.benefit_code;

      if (type === "PHYSICIAN_SIMPLE") {
        const res = await axios.get(
          `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/physicians`,
          { headers: getAuthHeaders() }
        );
        const rows = res?.data?.data?.physicians || [];
        setEditorState((prev) => ({ ...prev, physicianRows: mapPhysiciansForUi(rows) }));
      } else if (type === "PHYSICIAN_FEE") {
        const res = await axios.get(
          `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/physician-anes`,
          { headers: getAuthHeaders() }
        );
        const rows = res?.data?.data?.physician_anes || [];
        setEditorState((prev) => ({ ...prev, physicianRows: mapPhysiciansForUi(rows) }));
      } else if (type === "PHYSICIAN_AND_CPT") {
        const [physRes, cptRes] = await Promise.all([
          axios.get(
            `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/physicians`,
            { headers: getAuthHeaders() }
          ),
          axios.get(
            `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/cpt`,
            { headers: getAuthHeaders() }
          ),
        ]);
        setEditorState((prev) => ({
          ...prev,
          physicianRows: mapPhysiciansForUi(physRes?.data?.data?.physicians || []),
          cptRows: mapCptForUi(cptRes?.data?.data?.cpt || []),
        }));
      } else if (type === "MISC") {
        const res = await axios.get(
          `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/misc`,
          { headers: getAuthHeaders() }
        );
        const rows = res?.data?.data?.misc || [];
        setEditorState((prev) => ({
          ...prev,
          miscRows: rows.map((row) => ({ particulars: row.cpt_code || "", amount: String(row.amount ?? "") })),
        }));
      } else if (type === "RNB") {
        const res = await axios.get(
          `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/rnb`,
          { headers: getAuthHeaders() }
        );
        const rows = res?.data?.data?.rnb || [];
        setEditorState((prev) => ({
          ...prev,
          rnbRows: rows.map((row) => ({
            rnb_desc: row.rnb_desc || "",
            room_no: row.room_no || "",
            room_rate: String(row.room_rate ?? ""),
            no_of_days: String(row.no_of_days ?? ""),
            covered: String(row.covered ?? ""),
          })),
        }));
      } else if (type === "SURGICAL") {
        const res = await axios.get(
          `${BASE_URL}/reimbursement/transactions/${transNo}/surgical/details`,
          { headers: getAuthHeaders() }
        );
        setEditorState((prev) => ({
          ...prev,
          ruvRows: mapRuvForUi(res?.data?.data?.ruv || []),
          physicianRows: mapPhysiciansForUi(res?.data?.data?.physicians || []),
        }));
      } else if (type === "OPD_OR") {
        const res = await axios.get(
          `${BASE_URL}/reimbursement/transactions/${transNo}/opd-or/details`,
          { headers: getAuthHeaders() }
        );
        setEditorState((prev) => ({
          ...prev,
          rnbRows: (res?.data?.data?.rnb || []).map((row) => ({
            rnb_desc: row.rnb_desc || "",
            room_no: row.room_no || "",
            room_rate: String(row.room_rate ?? ""),
            no_of_days: String(row.no_of_days ?? ""),
            covered: String(row.covered ?? ""),
          })),
          cptRows: mapCptForUi(res?.data?.data?.cpt || []),
          physicianRows: mapPhysiciansForUi(res?.data?.data?.physicians || []),
          ruvRows: [],
        }));
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to load benefit details."));
    } finally {
      setEditorLoading(false);
    }
  };

  const saveEditor = async () => {
    if (!activeBenefit) return;

    const benefitCode = activeBenefit.benefit_code;
    const type = getBenefitEditorType(activeBenefit);
    const state = editorState || {};

    setEditorSaving(true);
    try {
      let computedAmount = Number(selectedBenefits[benefitCode]?.amount || 0);

      if (type === "PHYSICIAN_SIMPLE") {
        const rows = (state.physicianRows || []).map((row) => ({
          physician_id: row.physician_id || null,
          physician_name: String(row.physician_name || "").trim(),
          specialization: row.specialization || "",
          philhealth: toNumber(row.philhealth),
          amount: toNumber(row.amount),
        }));

        await axios.post(
          `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/physicians`,
          { rows, whole_philhealth: 0 },
          { headers: getAuthHeaders() }
        );

        computedAmount = rows.reduce((sum, row) => sum + toNumber(row.amount), 0);
      } else if (type === "PHYSICIAN_FEE") {
        const rows = (state.physicianRows || []).map((row) => ({
          benefit_code: benefitCode,
          benefit: activeBenefit.benefit_desc || "",
          physician_id: row.physician_id || null,
          physician_name: String(row.physician_name || "").trim(),
          specialization: row.specialization || "",
          philhealth: toNumber(row.philhealth),
          amount: toNumber(row.amount),
        }));

        await axios.post(
          `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/physician-anes`,
          { rows, status: "saved" },
          { headers: getAuthHeaders() }
        );

        computedAmount = rows.reduce((sum, row) => sum + toNumber(row.amount), 0);
      } else if (type === "PHYSICIAN_AND_CPT") {
        const physicianRows = (state.physicianRows || [])
          .map((row) => ({
            physician_id: row.physician_id || null,
            physician_name: String(row.physician_name || "").trim(),
            specialization: row.specialization || "",
            philhealth: toNumber(row.philhealth),
            amount: toNumber(row.amount),
          }))
          .filter((row) => row.physician_name);

        const cptRows = (state.cptRows || [])
          .map((row) => ({
            cpt_code: row.cpt_code,
            provider_cpt_code: row.provider_cpt_code || row.cpt_code,
            cpt_rate: toNumber(row.cpt_rate),
          }))
          .filter((row) => row.cpt_code);

        if (
          CPT_REQUIRED_PHYSICIAN_AND_CPT_BENEFITS.has(benefitCode) &&
          cptRows.length === 0
        ) {
          toast.error("Procedure (CPT) is required for this benefit.");
          setEditorSaving(false);
          return;
        }

        await Promise.all([
          axios.post(
            `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/physicians`,
            { rows: physicianRows, whole_philhealth: 0 },
            { headers: getAuthHeaders() }
          ),
          axios.post(
            `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/cpt`,
            { rows: cptRows },
            { headers: getAuthHeaders() }
          ),
        ]);

        const phys = getPhysicianTotals(physicianRows).net;
        const cpt = getCptTotal(cptRows);
        computedAmount = phys + cpt;
      } else if (type === "MISC") {
        const rows = (state.miscRows || []).map((row) => ({
          particulars: row.particulars,
          amount: toNumber(row.amount),
          benefit_code: benefitCode,
        }));
        await axios.post(
          `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/misc`,
          { rows },
          { headers: getAuthHeaders() }
        );
        computedAmount = rows.reduce((sum, row) => sum + toNumber(row.amount), 0);
      } else if (type === "RNB") {
        const rows = (state.rnbRows || []).map((row) => {
          const covered = toNumber(row.room_rate) * toNumber(row.no_of_days);
          return {
            rnb_desc: row.rnb_desc,
            room_no: row.room_no || "",
            no_of_days: toNumber(row.no_of_days),
            room_rate: toNumber(row.room_rate),
            covered,
          };
        });
        await axios.post(
          `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/rnb`,
          { rows },
          { headers: getAuthHeaders() }
        );
        computedAmount = rows.reduce((sum, row) => sum + toNumber(row.covered), 0);
      } else if (type === "SURGICAL") {
        const ruvRows = (state.ruvRows || []).map((row) => ({
          ruv_code: row.ruv_code,
          remarks: row.remarks || "",
          rnb: row.rnb || "",
          ruv_unit: toNumber(row.ruv_unit),
          amount: toNumber(row.amount),
          philhealth: toNumber(row.philhealth),
          or_amount: 0,
        }));
        const physicianRows = (state.physicianRows || []).map((row) => ({
          physician_id: row.physician_id || null,
          physician_name: String(row.physician_name || "").trim(),
          specialization: row.role || row.specialization || "",
          philhealth: toNumber(row.philhealth),
          amount: toNumber(row.amount),
        }));

        await Promise.all([
          axios.post(
            `${BASE_URL}/reimbursement/transactions/${transNo}/benefit-data/${benefitCode}/ruv`,
            { rows: ruvRows },
            { headers: getAuthHeaders() }
          ),
          axios.post(
            `${BASE_URL}/reimbursement/transactions/${transNo}/surgical/physicians`,
            { rows: physicianRows, whole_philhealth: 0 },
            { headers: getAuthHeaders() }
          ),
        ]);

        computedAmount =
          ruvRows.reduce((sum, row) => sum + toNumber(row.amount), 0) +
          getPhysicianTotals(physicianRows).net;
      } else if (type === "OPD_OR") {
        const rnbRows = (state.rnbRows || []).map((row) => ({
          rnb_desc: row.rnb_desc,
          room_no: row.room_no || "",
          no_of_days: toNumber(row.no_of_days),
          room_rate: toNumber(row.room_rate),
          covered: toNumber(row.room_rate) * toNumber(row.no_of_days),
        }));

        const cptRows = (state.cptRows || [])
          .map((row) => ({
            cpt_code: row.cpt_code,
            provider_cpt_code: row.provider_cpt_code || row.cpt_code,
            cpt_rate: toNumber(row.cpt_rate),
          }))
          .filter((row) => row.cpt_code);

        const physicianRows = (state.physicianRows || [])
          .map((row) => ({
            physician_id: row.physician_id || null,
            physician_name: String(row.physician_name || "").trim(),
            specialization: row.specialization || "",
            philhealth: toNumber(row.philhealth),
            amount: toNumber(row.amount),
          }))
          .filter((row) => row.physician_name);

        await axios.post(
          `${BASE_URL}/reimbursement/transactions/${transNo}/opd-or/save`,
          {
            rnb_rows: rnbRows,
            cpt_rows: cptRows,
            physician_rows: physicianRows,
            whole_philhealth: physicianRows.reduce(
              (sum, row) => sum + toNumber(row.philhealth),
              0
            ),
            icd_code: icdCode,
          },
          { headers: getAuthHeaders() }
        );

        computedAmount =
          getRnbCoveredTotal(rnbRows) +
          getCptTotal(cptRows) +
          getPhysicianTotals(physicianRows).net;
      }

      setSelectedBenefits((prev) => ({
        ...prev,
        [benefitCode]: {
          ...(prev[benefitCode] || {}),
          checked: true,
          amount: String(Number(computedAmount || 0).toFixed(2)),
        },
      }));

      setIsDirty(true);
      toast.success("Benefit details saved.");
      setEditorOpen(false);
    } catch (error) {
      if (error?.response?.status === 401) window.location.href = "/login";
      toast.error(getApiErrorMessage(error, "Failed to save benefit details."));
    } finally {
      setEditorSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse rounded-2xl border border-orange-100 bg-white p-5 shadow-sm space-y-5">
        <div className="space-y-3">
          <div className="h-5 w-36 rounded bg-gray-200" />
          <div className="h-10 w-full rounded-lg bg-gray-200 md:w-1/2" />
          <div className="h-3 w-72 rounded bg-gray-100" />
        </div>

        <div className="space-y-3">
          <div className="h-5 w-40 rounded bg-gray-200" />
          <div className="overflow-hidden rounded-xl border border-gray-100">
            <div className="h-10 w-full bg-gray-100" />
            {[...Array(6)].map((_, idx) => (
              <div key={`benefit-skeleton-${idx}`} className="grid grid-cols-12 gap-3 border-t border-gray-100 px-3 py-3">
                <div className="col-span-1 h-4 rounded bg-gray-200" />
                <div className="col-span-2 h-4 rounded bg-gray-200" />
                <div className="col-span-3 h-4 rounded bg-gray-200" />
                <div className="col-span-2 h-4 rounded bg-gray-200" />
                <div className="col-span-2 h-4 rounded bg-gray-200" />
                <div className="col-span-2 h-8 rounded bg-gray-200" />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <div className="h-10 w-48 rounded-lg bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm space-y-5">
      {/* ICD Code */}
      <div>
        <h2 className="mb-3 text-lg font-semibold text-gray-900">ICD Code</h2>
        <div className="flex gap-3 md:w-1/2">
          <input
            value={icdCode}
            onChange={(e) => {
              setIcdCode(e.target.value);
              setIsDirty(true);
            }}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:border-orange-500 focus:outline-none"
            placeholder="Enter ICD code (e.g. J18.9)"
          />
        </div>
        <p className="mt-1 text-xs text-gray-400">
          Enter the ICD-10 diagnosis code for this reimbursement claim.
        </p>
      </div>

      {/* Benefit Selection */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Benefit Selection
          </h2>
          <div className="flex flex-wrap items-center justify-end gap-2 text-sm text-gray-600">
            <span>
              <strong>{selectedCount}</strong> selected
            </span>
            <span className="font-semibold text-green-700">
              Total: {formatCurrency(totalAmount)}
            </span>
            <button
              onClick={handleSave}
              disabled={saving || selectedCount === 0}
              className="inline-flex items-center gap-1 rounded border border-orange-200 bg-orange-50 px-2 py-1 text-xs font-semibold text-orange-700 hover:bg-orange-100 disabled:opacity-60"
            >
              {saving ? (
                <FontAwesomeIcon icon={faSpinner} spin className="text-xs" />
              ) : (
                <FontAwesomeIcon icon={faSave} className="text-xs" />
              )}
              Save Benefits &amp; ICD Code
            </button>
            <button
              onClick={loadData}
              className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
            >
              <FontAwesomeIcon icon={faSyncAlt} className="text-xs" />
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-10 px-3 py-2 text-center font-semibold text-gray-600">
                  ✓
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">
                  Code
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">
                  Description
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">
                  Category
                </th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">
                  Member Limit
                </th>
                <th className="w-40 px-3 py-2 text-right font-semibold text-gray-600">
                  Claim Amount (PHP)
                </th>
                <th className="w-32 px-3 py-2 text-center font-semibold text-gray-600">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 bg-white">
              {allBenefits.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-gray-400"
                  >
                    No reference benefits found.
                  </td>
                </tr>
              )}
              {allBenefits.map((b) => {
                const isSelected = !!selectedBenefits[b.benefit_code]?.checked;
                const limit = benefitLimits[b.benefit_code];

                return (
                  <tr
                    key={b.benefit_code}
                    className={`cursor-pointer transition-colors ${
                      isSelected ? "bg-orange-50" : "hover:bg-gray-50"
                    }`}
                    onClick={() => toggleBenefit(b.benefit_code)}
                  >
                    <td className="px-3 py-2 text-center">
                      <span
                        className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                          isSelected
                            ? "bg-orange-500 text-white"
                            : "border border-gray-300 bg-white text-transparent"
                        }`}
                      >
                        ✓
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-gray-700">
                      {b.benefit_code}
                    </td>
                    <td className="px-3 py-2 text-gray-800">
                      {b.benefit_desc}
                    </td>
                    <td className="px-3 py-2 text-gray-500">
                      {b.benefit_category || "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">
                      {limit != null ? (
                        typeof limit === "number" ? (
                          formatCurrency(limit)
                        ) : (
                          <span className="text-xs italic">{limit}</span>
                        )
                      ) : (
                        "—"
                      )}
                    </td>
                    <td
                      className="px-3 py-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={selectedBenefits[b.benefit_code]?.amount || ""}
                        onChange={(e) =>
                          setAmount(b.benefit_code, e.target.value)
                        }
                        disabled={!isSelected}
                        placeholder="0.00"
                        className="w-full rounded-lg border border-gray-200 px-2 py-1 text-right text-sm disabled:bg-gray-50 disabled:text-gray-400 focus:border-orange-500 focus:outline-none"
                      />
                    </td>
                    <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700 hover:bg-orange-100"
                        onClick={() => openEditor(b)}
                      >
                        <FontAwesomeIcon icon={faPen} className="text-[10px]" />
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {selectedCount > 0 && (
              <tfoot>
                <tr className="border-t-2 border-orange-200 bg-orange-50">
                  <td colSpan={5} />
                  <td className="px-3 py-2 text-right text-sm font-semibold text-gray-700">
                    Total Claim:
                  </td>
                  <td className="px-3 py-2 text-right text-sm font-bold text-green-700">
                    {formatCurrency(totalAmount)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || selectedCount === 0}
          className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
        >
          {saving ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <FontAwesomeIcon icon={faSave} />
          )}
          Save Benefits &amp; ICD Code
        </button>
      </div>

      <BenefitEditorModal
        open={editorOpen}
        benefit={activeBenefit}
        editorType={activeEditorType}
        state={editorState}
        loading={editorLoading}
        saving={editorSaving}
        onClose={() => setEditorOpen(false)}
        onSave={saveEditor}
        onStateChange={setEditorState}
        loadPhysicianOptions={loadPhysicianOptions}
        loadCptOptions={loadCptOptions}
        loadRvuOptions={loadRvuOptions}
      />
    </div>
  );
};

export default RiBenefitsTab;
